import { ICON, ICON_COLOR, ICON_SIZE, ICON_TRANSFORM } from 'gcs-common/constants/IconConstants';
import { useCallback, useEffect } from 'react';

import {
  useGetETSTicketAttachmentsQuery,
  useGetETSTicketRatingQuery,
} from 'gcs-common/clients/api/entities/tickets/tickets-api';
import { refetchETSTicketDetails } from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import { useGetETSTicketNotificationSelector } from 'gcs-common/clients/api/entities/tickets/tickets-api-selectors';
import IconComponent from 'gcs-common/components/Icon/Icon';
import NotificationBadge from 'gcs-common/components/Notifications/NotificationBadge/NotificationBadge';
import TicketStatus from 'gcs-common/components/Tickets/TicketStatus/TicketStatus';
import { friendlyDate } from 'gcs-common/helper/dateHelper';
import { TICKET_INPUT_CHANNELS } from 'gcs-common/slices/tickets/ticketsConstants';
import type { Response } from 'gcs-common/types/typeHelper';
import moment from 'moment';
import TicketComments from '../TicketComments/TicketComments';
import TicketRating from '../TicketRating/TicketRating';
import TicketAttachmentsList from './TicketAttachmentsList/TicketAttachmentsList';
import TicketInfo from './TicketInfo/TicketInfo';
import TicketParts from './TicketParts/TicketParts';
import styles from './styles.module.scss';

interface TicketDetailsProps {
  ticket: Response<'/chat/ets/tickets/{ticket_id}', 'get'>;
  onClose: VoidFunction;
}

const TicketDetails = (props: TicketDetailsProps) => {
  const { onClose, ticket } = props;

  const { data: attachmentsData } = useGetETSTicketAttachmentsQuery({
    path: {
      ticket_id: ticket.id,
    },
  });
  const attachments = attachmentsData?.attachments;

  const ticketNotification = useGetETSTicketNotificationSelector(ticket.id);
  const { data: ticketRating } = useGetETSTicketRatingQuery({
    path: {
      ticket_id: ticket.id,
    },
  });

  const isCraftsmanTicket = ticket?.inputChannel === TICKET_INPUT_CHANNELS.craftsman.key;

  const refetchData = useCallback(() => {
    if (!ticket.id) return;
    refetchETSTicketDetails(ticket.id);
  }, [ticket.id]);

  useEffect(() => {
    refetchData();
  }, [refetchData, ticketNotification?.updatedAt]);

  return (
    <div className={styles.ticketWrapper} key={ticket.id}>
      <div className={styles.ticketDetailsHeader}>
        <button onClick={onClose} type="button">
          <IconComponent Icon={ICON.ARROW} size={ICON_SIZE.LARGE} transform={ICON_TRANSFORM.ROTATE_180} />
        </button>
        <span>{ticket?.title}</span>
      </div>
      {ticket && (
        <div className={styles.container}>
          <div>
            <div className={styles.ticketDetailsWrapper}>
              <div className={styles.ticketDetailLeft}>
                <span>Erstelldatum</span>
                <span>Ticket-ID</span>
                <span>
                  {ticketNotification && moment(ticket.statusUpdatedAt).isAfter(ticketNotification.lastViewedAt) ? (
                    <NotificationBadge />
                  ) : (
                    <div className={styles.status}> ETS </div>
                  )}
                </span>
              </div>
              <div className={styles.ticketDetailRight}>
                <span>{friendlyDate(ticket.createdAt)}</span>
                <span>{ticket.externalJiraKey}</span>
                <TicketStatus status={ticket.status} />
              </div>
            </div>
          </div>

          {isCraftsmanTicket && (
            <div className={styles.craftsmanTicketWarning}>
              <IconComponent Icon={ICON.INFORMATION} size={ICON_SIZE.LARGE} color={ICON_COLOR.PURPLE} />
              <div>Diese Anfrage wurde von einem Kunden erstellt, daher kannst du sie nur ansehen.</div>
            </div>
          )}

          {ticket.status === 'done' && (
            <div>
              <TicketParts ticketId={ticket.id} />
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.subTitle}>Beschreibung</div>
            <div>{ticket.description}</div>
          </div>

          {!isCraftsmanTicket && ticketRating && (ticket.status === 'wont_do' || ticket.status === 'done') && (
            <TicketRating
              ticketId={ticket.id}
              ticketStatus={ticket.status}
              comment={ticketRating.comment}
              rating={ticketRating.rating}
              onRatingAdded={refetchData}
            />
          )}

          {attachments && attachments.length > 0 && (
            <div className={styles.section}>
              <div className={styles.subTitle}>Anhänge</div>
              <TicketAttachmentsList ticketId={ticket.id} attachments={attachments} />
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.subTitle}>Kommentare</div>
            <TicketComments ticketId={ticket.id} readOnly={isCraftsmanTicket} />
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <div className={styles.subTitle}>Allgemeine Informationen</div>
            <div>
              <TicketInfo ticket={ticket} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetails;
